import { useContext } from 'react';
import cn from 'classnames';

import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';
import { Col, Row } from '@guestyci/foundation/Layout';
import t from '@guestyci/localize/t.macro';
import { RadioGroup, RadioGroupItem } from '@guestyci/arc';

import { WebsiteSettingsContext } from 'context/WebsiteSettingsContext';
import CancellationPolicy from 'components/CancellationPolicy';
import PriceConverter from 'components/PriceConverter';
import useTotalPrice from 'hooks/useTotalPrice';

const useStyles = createStyles(() => ({
  ratePlanItem: {
    margin: '5px 0px',
    width: '100%',
    border: '1px solid #D4D8E1',
    cursor: 'pointer',
    position: 'relative',
    padding: 10,
    '&[data-chosen="true"]': {
      border: `1px solid`,
      borderColor: ({ colors }) => colors?.mainButtonsAndLinks || '#1BBCEF',
      background: ({ colors }) => colors?.mainButtonsAndLinks || '#1BBCEF',
      color: '#fff',
      '& div': {
        color: '#fff',
      },
    },
  },
  radioButton: {
    '& button': {
      marginRight: 10,
    },
  },
  price: {
    textAlign: 'right',
  },
}));

const CancellationPolicyWithRadio = ({ ratePlan, selectedPlan, onChange, defaultCheckInTime }) => {
  const {
    contentConfiguration: { colorTheme },
  } = useContext(WebsiteSettingsContext);
  const styles = useStyles(colorTheme);
  const {
    name,
    money: { subTotalPrice, currency, hostPayout },
  } = ratePlan;
  const { price } = useTotalPrice({ beforeTaxesPrice: subTotalPrice, totalPrice: hostPayout });

  const value = JSON.stringify(ratePlan);

  return (
    <Row align="center" data-qa="rate-plan-card">
      <RadioGroup value={selectedPlan} onValueChange={onChange}>
        <div className="gst-flex gst-items-center gst-space-x-2">
          <RadioGroupItem
            className={cn(styles.radioButton, 'radio')}
            value={value}
            id={name}
            aria-label={name || t('Rate plan option')}
          />
          <Col className={styles.ratePlanItem} data-chosen={selectedPlan === value}>
            <CancellationPolicy ratePlan={ratePlan} defaultCheckInTime={defaultCheckInTime} />
            <TextField variant="h5" bold className={styles.price}>
              <PriceConverter amount={price} currency={currency} />
            </TextField>
          </Col>
        </div>
      </RadioGroup>
    </Row>
  );
};

const CancellationPolicyWithoutRadioRender = ({ ratePlan, defaultCheckInTime, withAccordion }) => (
  <Col>
    <CancellationPolicy ratePlan={ratePlan} defaultCheckInTime={defaultCheckInTime} withAccordion={withAccordion} />
  </Col>
);

const RatePlanV2 = ({ ratePlan, withRadio, selectedPlan, onChange, defaultCheckInTime, withAccordion }) => {
  if (!ratePlan || !ratePlan.cancellationPolicy) return null;

  return (
    <>
      {withRadio ? (
        <CancellationPolicyWithRadio
          ratePlan={ratePlan}
          selectedPlan={selectedPlan}
          onChange={onChange}
          defaultCheckInTime={defaultCheckInTime}
        />
      ) : (
        <CancellationPolicyWithoutRadioRender
          ratePlan={ratePlan}
          defaultCheckInTime={defaultCheckInTime}
          withAccordion={withAccordion}
        />
      )}
    </>
  );
};

export default RatePlanV2;
